import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { useTranslation } from "@redotech/redo-i18n";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down_filled.svg";
import { Avatar } from "@redotech/redo-web/arbiter-components/avatars/avatar";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoListDropdown } from "@redotech/redo-web/arbiter-components/list/redo-list-dropdown";
import { Flex } from "@redotech/redo-web/flex";
import { getGravatarUrlFromEmail } from "@redotech/redo-web/gravatar-utils";
import { Text } from "@redotech/redo-web/text";
import { useState, type ReactNode } from "react";
import * as navbarCss from "./version-four-navbar.module.css";

export interface VersionFourNavbarDisplayProps<T extends string = string> {
  customerEmail?: string;
  customerName?: string;
  leftContent?: ReactNode;
  logoHref?: string;
  logoUrl?: string;
  menuItems: RedoListItem<T>[];
  onMenuItemSelected: (item: RedoListItem<T>) => void;
  orderName?: string;
  storeName?: string;
}

export function VersionFourNavbarDisplay<T extends string = string>({
  customerEmail,
  customerName,
  leftContent,
  logoHref,
  logoUrl,
  menuItems,
  onMenuItemSelected,
  orderName,
  storeName,
}: VersionFourNavbarDisplayProps<T>) {
  const { t } = useTranslation();
  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const screen = useScreenType();

  return (
    <Flex
      align="center"
      bgColor={screen === ScreenType.DESKTOP ? "primary" : "secondary"}
      borderBottomWidth="1px"
      borderColor="secondary"
      borderStyle="solid"
      className={navbarCss.navBar}
      data-testid="navbar"
      dir="row"
      justify="space-between"
      position="sticky"
      px={screen === ScreenType.DESKTOP ? "2xl" : "lg"}
      py="lg"
      top="0"
    >
      <Flex align="center" basis="33.33%" dir="row" gap="lg">
        {leftContent}
        {screen === ScreenType.DESKTOP && orderName && (
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            {t("Order {{orderName}}", { orderName })}
          </Text>
        )}
      </Flex>

      <Flex basis="33.33%" justify="center">
        {renderBrand({
          logoHref,
          logoUrl,
          storeName,
          logoAlt: t("Store logo"),
        })}
      </Flex>

      <Flex align="center" basis="33.33%" dir="row" gap="sm" justify="flex-end">
        <RedoListDropdown
          dropdownAnchor={dropdownButtonRef}
          dropdownOpen={dropdownOpen}
          items={menuItems}
          itemSelected={({ item }) => onMenuItemSelected(item)}
          setDropdownOpen={setDropdownOpen}
          size="xs"
        />

        <Flex
          align="center"
          className={navbarCss.userMenuTrigger}
          gap="sm"
          onClick={() => setDropdownOpen(!dropdownOpen)}
          ref={setDropdownButtonRef}
        >
          <Flex align="center" dir="row" gap="sm">
            <Avatar
              alt={customerName || customerEmail || ""}
              imageUrl={
                customerName
                  ? null
                  : customerEmail
                    ? getGravatarUrlFromEmail(customerEmail)
                    : null
              }
              name={customerName}
            />
            {screen === ScreenType.DESKTOP && customerName && (
              <Text fontSize="sm" fontWeight="bold">
                {customerName}
              </Text>
            )}
          </Flex>
          <ChevronDownIcon className={navbarCss.chevronIcon} />
        </Flex>
      </Flex>
    </Flex>
  );
}

// Prefers the logo image; falls back to the store name in the same slot when
// the merchant hasn't uploaded a logo (without this, the <img> renders with an
// empty src and the browser refetches the current page as the image).
function renderBrand({
  logoHref,
  logoUrl,
  storeName,
  logoAlt,
}: {
  logoHref?: string;
  logoUrl?: string;
  storeName?: string;
  logoAlt: string;
}): ReactNode {
  if (logoUrl) {
    const img = (
      <img
        alt={logoAlt}
        className={navbarCss.navBarLogo}
        id="navbar-logo"
        src={logoUrl}
      />
    );
    return logoHref ? (
      <a href={logoHref} rel="noreferrer" target="_blank">
        {img}
      </a>
    ) : (
      img
    );
  }
  if (!storeName) {
    return null;
  }
  const text = (
    <Text
      className={navbarCss.navBarStoreName}
      fontSize="md"
      fontWeight="semibold"
      textColor="primary"
    >
      {storeName}
    </Text>
  );
  return logoHref ? (
    <a
      className={navbarCss.navBarStoreNameLink}
      href={logoHref}
      rel="noreferrer"
      target="_blank"
    >
      {text}
    </a>
  ) : (
    text
  );
}
